import { Menu, MenuOpen } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/joy";
import { useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";

export const EVENTS_LIST_ID = "events-list";

// Below this width the events list starts collapsed and collapses again whenever an event is
// picked, so the selected event gets the room.
const COMPACT_QUERY = "(max-width: 1099px)";

export const isCompactViewport = () =>
  typeof window !== "undefined" && window.matchMedia(COMPACT_QUERY).matches;

/**
 * Collapses the events list when the selected event changes on a compact viewport. The initial
 * selection is ignored: the caller decides the starting state, since the list is remounted
 * when moving between the events index, create and detail routes.
 */
export const useCollapseEventsListOnSelect = (
  selectedUuid: string | undefined,
  collapse: () => void,
) => {
  const isCompact = useMediaQuery({ query: COMPACT_QUERY });
  const previousUuid = useRef(selectedUuid);

  useEffect(() => {
    if (previousUuid.current === selectedUuid) return;
    previousUuid.current = selectedUuid;
    if (selectedUuid && isCompact) collapse();
  }, [selectedUuid, isCompact, collapse]);
};

export default function EventsListToggle({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}) {
  const label = isOpen ? "Collapse events list" : "Expand events list";

  return (
    <Tooltip title={label}>
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls={EVENTS_LIST_ID}
        onClick={(e) => {
          // The surrounding header also toggles on click.
          e.stopPropagation();
          onToggle();
        }}
      >
        {isOpen ? <MenuOpen /> : <Menu />}
      </IconButton>
    </Tooltip>
  );
}
